// distance in real units using provided px->unit scale
export const calculateDistance = (p1, p2, scale) => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const pixelDistance = Math.hypot(dx, dy);
  return pixelDistance * scale;
};

export const calculateAngle = (center, p1, p2) => {
  const a1 = Math.atan2(p1.y - center.y, p1.x - center.x);
  const a2 = Math.atan2(p2.y - center.y, p2.x - center.x);
  let deg = Math.abs((a2 - a1) * (180 / Math.PI));
  return deg > 180 ? 360 - deg : deg;
};

// points: [{x,y}] in current canvas coords
// scale:        real units for the reference line (e.g., 20 for "20 mm") or 0/undefined
// scaleLength:  reference line length in *canvas pixels* (e.g., 150) or 0/undefined
// fabricCanvasOrRef: fabric.Canvas instance OR a ref { current: fabric.Canvas }
export function calculatePolygonArea(
  points,
  scale,
  scaleLength,
  fabricCanvasOrRef
) {
  if (!points || points.length < 3) return 0;

  // Fabric instance or ref
  const fc =
    fabricCanvasOrRef && fabricCanvasOrRef.current
      ? fabricCanvasOrRef.current
      : fabricCanvasOrRef;

  // If we can read the bg transform, convert canvas points -> image pixel points.
  // If not, we’ll fall back to using canvas coords as-is.
  let toImagePx = (p) => p;

  if (fc && fc.backgroundImage) {
    const bg = fc.backgroundImage;

    // normalize transform
    const sx = bg.scaleX || 1;
    const sy = bg.scaleY || 1;
    const angle = ((bg.angle || 0) * Math.PI) / 180;

    // left/top adjusted if origin is center
    let left = bg.left || 0;
    let top = bg.top || 0;
    if (bg.originX === "center" || bg.originY === "center") {
      const sw = bg.getScaledWidth();
      const sh = bg.getScaledHeight();
      if (bg.originX === "center") left -= sw / 2;
      if (bg.originY === "center") top -= sh / 2;
    }

    toImagePx = (p) => {
      // translate to bg local space
      let x = p.x - left;
      let y = p.y - top;

      // inverse rotation
      if (angle) {
        const c = Math.cos(-angle);
        const s = Math.sin(-angle);
        const xr = x * c - y * s;
        const yr = x * s + y * c;
        x = xr;
        y = yr;
      }

      // inverse scale -> original image pixels
      return { x: x / sx, y: y / sy };
    };
  }

  // Shoelace in IMAGE pixel space (or canvas space if no bg)
  let sum = 0;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = toImagePx(points[j]);
    const b = toImagePx(points[i]);
    sum += a.x * b.y - b.x * a.y;
  }
  const areaPx2 = Math.abs(sum) / 2;

  // ---- Units handling (your exact call: scale, scaleLength) ----
  // If both provided, convert px² -> units² using (units/px)²,
  // where units-per-pixel = scale / scaleLength.
  const s = Number(scale) || 0; // e.g., 20 (mm)
  const refPx = Number(scaleLength) || 0; // e.g., 150 (px)
  if (s > 0 && refPx > 0) {
    const upp = s / refPx; // units per pixel
    return areaPx2 * upp * upp; // -> units² (e.g., mm²)
  }

  // No scale => return raw pixels² (matches ImageJ)
  return areaPx2;
}

export const calculatePolygonPerimeter = (points, distFn) => {
  if (!points || points.length < 2) return 0;
  let per = 0;
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length;
    per += distFn(points[i], points[j]);
  }
  return per;
};

// distance from point P to segment AB; also returns the projection point and t
export function pointToSegmentInfo(P, A, B) {
  const vx = B.x - A.x;
  const vy = B.y - A.y;
  const wx = P.x - A.x;
  const wy = P.y - A.y;
  const vv = vx * vx + vy * vy || 1e-9;
  let t = (wx * vx + wy * vy) / vv;
  t = Math.max(0, Math.min(1, t));
  const proj = { x: A.x + t * vx, y: A.y + t * vy };
  const dx = proj.x - P.x;
  const dy = proj.y - P.y;
  return { dist: Math.hypot(dx, dy), proj, t };
}

// find closest edge on a closed polygon to click point
export function findClosestEdge(points, clickPt) {
  let best = { idx: -1, dist: Infinity, proj: null, t: 0 };
  const n = points.length;
  if (n < 2) return best;
  for (let i = 0; i < n; i++) {
    const A = points[i];
    const B = points[(i + 1) % n];
    const info = pointToSegmentInfo(clickPt, A, B);
    if (info.dist < best.dist)
      best = { idx: i, dist: info.dist, proj: info.proj, t: info.t };
  }
  return best; // idx is the edge (i -> i+1), proj is the cut point
}

// Re-open a CLOSED polygon at a chosen edge point.
// Returns a new polygon object with isClosed=false and points reordered.
export function reopenPolygonAt(pol, edgeIndex, cutPoint) {
  const pts = pol.points.slice(); // copy
  const n = pts.length;
  const i = edgeIndex;
  const next = (i + 1) % n;

  // Insert the cut point into the edge (i -> next)
  const newPts = pts.slice(0, i + 1).concat([cutPoint], pts.slice(i + 1));

  // The path must start and end at the same cut point (duplicated) to preserve shape outline,
  // but as an OPEN polyline we keep both ends equal to cutPoint WITHOUT closing the shape.
  // Example:
  //   Start at cutPoint, then go through (next ... end, 0 ... i), and finally end at cutPoint.
  const start = i + 1; // cutPoint index in newPts
  const ordered = [newPts[start]] // begin at cutPoint
    .concat(newPts.slice(start + 1)) // (after cut -> end)
    .concat(newPts.slice(0, start)) // (0 -> i)
    .concat([newPts[start]]); // end at cutPoint (duplicate as last point)

  return {
    ...pol,
    isClosed: false,
    points: ordered,
  };
}

export function reorderPolygonPoints(points, clickedIndex) {
  if (!Array.isArray(points) || points.length < 3) return points;
  if (clickedIndex <= 0) return points;

  // rotate so clicked index becomes the new start
  const before = points.slice(clickedIndex);
  const after = points.slice(0, clickedIndex);
  return [...before, ...after];
}
